import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  normalizeLanguage,
} from "../../i18n";

/**
 * Compact language picker that keeps floorplanner in sync with boodil-client
 * via the shared `boni_lang` localStorage key.
 */
export const LanguageSelector: React.FC<{
  className?: string;
  compact?: boolean;
}> = ({ className = "", compact = true }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentLanguage = normalizeLanguage(i18n.language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = async (language: SupportedLanguage) => {
    await i18n.changeLanguage(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    setIsOpen(false);
  };

  const compactLabel = currentLanguage.startsWith("en-")
    ? currentLanguage.slice(3).toUpperCase()
    : currentLanguage.toUpperCase();

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t("language.label")}
        onClick={() => setIsOpen((open) => !open)}
        className={
          compact
            ? "relative flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-medium uppercase text-gray-700 transition-colors hover:bg-gray-50"
            : "rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 hover:border-gray-400"
        }
      >
        {compact ? compactLabel : t(`language.${currentLanguage}`)}
      </button>
      {isOpen && (
        <ul
          role="listbox"
          aria-label={t("language.label")}
          className="absolute left-0 bottom-full z-50 mb-1 min-w-[140px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {SUPPORTED_LANGUAGES.map((language) => (
            <li key={language}>
              <button
                type="button"
                role="option"
                aria-selected={currentLanguage === language}
                onClick={() => handleChange(language)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  currentLanguage === language
                    ? "bg-gray-50 font-medium"
                    : "text-gray-800"
                }`}
              >
                {t(`language.${language}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
